import { useEffect, useRef, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router"
import { useMutation } from "@tanstack/react-query"
import QrScanner from "qr-scanner"
import ModuleLayout from "../../components/admin/ModuleLayout"
import Alert from "@/components/ui/alert/Alert"
import { getInputClasses } from "@/components/form/input/InputField"
import { cccdAttendance, type AttendeeTicket, type AttendeeCheckIn } from "@/lib/api/cccdAttendanceClient"
import { useAttendanceSummary } from "@/hooks/useAttendanceSummary"
import { daysBetween, defaultEventDay, formatDay, rememberScope } from "@/lib/checkinEvents"
import { useCheckinQueue } from "@/hooks/useCheckinQueue"
import { isNetworkError, queueKey, type QueuedCheckin } from "@/lib/checkinQueue"
import { getErrorMessage, getErrorStatus } from "@/lib/errors"
import { toastError } from "@/lib/toast"

type Scope = { eventId?: string; regionalEventId?: string }
type ResultTicket = AttendeeTicket & { team: { id: string; name: string }; checkIn: AttendeeCheckIn | null }
type Tab = "scan" | "document" | "onsite"
type Enqueue = (item: Omit<QueuedCheckin, "queuedAt">) => boolean

/** Conteo de ingresos de este dispositivo por evento y día: reconoce el trabajo en la puerta sin pedir red. */
function readCount(key: string): number {
  try {
    return Number(localStorage.getItem(key)) || 0
  } catch {
    return 0
  }
}

function writeCount(key: string, value: number) {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    // Sin almacenamiento: el conteo queda solo en pantalla.
  }
}

/** Vibración corta al confirmar: en la puerta, con sol y ruido, el cue visual solo no alcanza. */
function buzz() {
  try {
    navigator.vibrate?.(80)
  } catch {
    // No soportado (iOS Safari): el cue visual sigue funcionando.
  }
}

const OFFLINE_MESSAGE = "Sin conexión. Esta acción necesita internet; inténtalo de nuevo cuando vuelvas a conectarte."

function networkAware(err: unknown): string {
  return isNetworkError(err) ? OFFLINE_MESSAGE : getErrorMessage(err)
}

const inputCls = getInputClasses({})
// Shared affordance for every button on this page: theme-consistent shadow, hover
// transition, and the system's brand-blue focus ring.
const btnFocus = "shadow-theme-xs transition focus:outline-hidden focus:ring-3 focus:ring-brand-500/20"
const bigBtn = `w-full rounded-lg px-4 py-3 text-base font-medium disabled:opacity-60 ${btnFocus}`
// One entrance moment for every "you're checked in" confirmation, so the door-line
// staff sees the same split-second cue everywhere the flow confirms an entry.
const checkinConfirmPop = "motion-safe:animate-[checkin-confirm-pop_380ms_cubic-bezier(0.16,1,0.3,1)]"

function cameraErrorMessage(err: unknown): string {
  const name = err instanceof DOMException ? err.name : ""
  if (name === "NotAllowedError") return "Bloqueaste el permiso de cámara. Actívalo en la configuración del navegador o usa la búsqueda por cédula."
  if (name === "NotFoundError") return "No se encontró ninguna cámara en este dispositivo. Usa la búsqueda por cédula."
  if (name === "NotReadableError") return "La cámara está siendo usada por otra aplicación. Ciérrala e inténtalo de nuevo, o usa la búsqueda por cédula."
  return "No se pudo activar la cámara. Verifica los permisos de cámara del navegador o usa la búsqueda por cédula."
}

/**
 * Un QR escaneado puede no ser un boleto (cartel, patrocinador, etc.), lo que hace
 * que `:qrToken` nunca matchee y caiga en el 404 genérico de Hono en vez del
 * errorResponse('Ticket not found') de la ruta. Se colapsa junto con ese 404 real
 * en un solo mensaje amigable; el 410 (revocado) sí es información accionable
 * para el staff y conserva su propio mensaje.
 */
function scanErrorMessage(err: unknown): string {
  if (getErrorStatus(err) === 410) return "Este boleto fue revocado y ya no es válido."
  if (getErrorStatus(err) === 404) return "Este código QR no corresponde a un boleto válido. Intenta de nuevo o usa la búsqueda por cédula."
  return getErrorMessage(err)
}

function TicketCard({
  ticket,
  eventDate,
  method,
  onCheckedIn,
  onAutoAdvance,
  enqueue,
  onEntry,
}: {
  ticket: ResultTicket
  eventDate: string
  method: "qr_scan" | "document_lookup"
  onCheckedIn: (checkIn: AttendeeCheckIn) => void
  /** Cuando se da, se llama ~1s después de confirmar la entrada (no al marcar manilla) para volver a escanear sin un toque manual. */
  onAutoAdvance?: () => void
  /** Guarda la entrada en la cola local cuando no hay red. */
  enqueue: Enqueue
  /** Se llama una vez por cada ingreso nuevo (confirmado o guardado en cola). */
  onEntry?: () => void
}) {
  const [wristbandGiven, setWristbandGiven] = useState(false)
  const [error, setError] = useState("")
  const [queued, setQueued] = useState(false)

  const checkinMutation = useMutation({
    mutationFn: () =>
      cccdAttendance.checkin({ attendeeId: ticket.id, eventDate, method, wristbandGiven }),
    onSuccess: (checkIn) => {
      buzz()
      onEntry?.()
      onCheckedIn(checkIn)
      if (onAutoAdvance) setTimeout(onAutoAdvance, 1000)
    },
    onError: (err) => {
      if (isNetworkError(err)) {
        enqueue({
          key: queueKey(ticket.id, eventDate),
          attendeeId: ticket.id,
          eventDate,
          method,
          wristbandGiven,
          label: `${ticket.firstName} ${ticket.lastName}`,
        })
        buzz()
        onEntry?.()
        setQueued(true)
        if (onAutoAdvance) setTimeout(onAutoAdvance, 1000)
        return
      }
      const message = getErrorMessage(err)
      setError(message)
      toastError(message)
    },
  })

  const wristbandMutation = useMutation({
    mutationFn: (given: boolean) => cccdAttendance.setWristband(ticket.checkIn!.id, given),
    onSuccess: (checkIn) => onCheckedIn(checkIn),
    onError: (err) => toastError(getErrorMessage(err)),
  })

  return (
    <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {ticket.attendeeType === "member" ? "Integrante de equipo" : "Acompañante"}
        </p>
        <h3 className="mt-0.5 text-lg font-semibold text-gray-900 dark:text-white">
          {ticket.firstName} {ticket.lastName}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {ticket.documentType} {ticket.documentNumber} · Equipo: {ticket.team?.name ?? "—"}
        </p>
      </div>

      {ticket.status === "revoked" && (
        <Alert variant="error" title="Boleto revocado" message="Este boleto ya no es válido." />
      )}

      {ticket.checkIn ? (
        <div className={`space-y-3 ${checkinConfirmPop}`}>
          <Alert
            variant="warning"
            title="Este boleto ya ingresó hoy"
            message={`Entrada marcada a las ${new Date(ticket.checkIn.checkedInAt!).toLocaleTimeString("es-CO")}. Verifica que no sea un reuso antes de dejar pasar a la persona.`}
          />
          <button
            type="button"
            disabled={wristbandMutation.isPending}
            aria-pressed={!!ticket.checkIn.wristbandGivenAt}
            onClick={() => wristbandMutation.mutate(!ticket.checkIn!.wristbandGivenAt)}
            className={`${bigBtn} border ${ticket.checkIn.wristbandGivenAt ? "border-success-300 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-900/10" : "border-gray-300 dark:border-gray-700"}`}
          >
            {wristbandMutation.isPending
              ? "Guardando..."
              : ticket.checkIn.wristbandGivenAt
                ? "✓ Manilla entregada (tocar para deshacer)"
                : "Marcar manilla entregada"}
          </button>
        </div>
      ) : queued ? (
        <div className={checkinConfirmPop}>
          <Alert
            variant="warning"
            title="Entrada guardada sin conexión"
            message="Quedó en cola en este dispositivo y se enviará al recuperar la conexión. Puedes dejar pasar a la persona."
          />
        </div>
      ) : (
        <div className="space-y-3">
          <label className="flex min-h-11 items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
            <input type="checkbox" className="size-5" checked={wristbandGiven} onChange={(e) => setWristbandGiven(e.target.checked)} />
            Entregar manilla ahora
          </label>
          <button
            type="button"
            disabled={checkinMutation.isPending || ticket.status === "revoked"}
            onClick={() => { setError(""); checkinMutation.mutate() }}
            className={`${bigBtn} bg-brand-500 text-white hover:bg-brand-600`}
          >
            {checkinMutation.isPending ? "Confirmando..." : "Confirmar entrada"}
          </button>
        </div>
      )}

      {error && <Alert variant="error" title="No se pudo confirmar" message={error} />}
    </div>
  )
}

function ScanTab({ eventDate, enqueue, onEntry }: { eventDate: string; enqueue: Enqueue; onEntry: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const scannerRef = useRef<QrScanner | null>(null)
  const busyRef = useRef(false)
  const [cameraError, setCameraError] = useState("")
  const [ticket, setTicket] = useState<ResultTicket | null>(null)
  const [loading, setLoading] = useState(false)
  const [offlineNote, setOfflineNote] = useState("")
  // El callback del scanner se crea una sola vez al montar: sin esto leería un eventDate y un
  // enqueue viejos si el staff cambia el día con la cámara ya abierta.
  const latest = useRef({ eventDate, enqueue, onEntry })
  latest.current = { eventDate, enqueue, onEntry }

  useEffect(() => {
    if (!videoRef.current) return
    const scanner = new QrScanner(
      videoRef.current,
      (result) => handleDecoded(result.data),
      { highlightScanRegion: true, highlightCodeOutline: true, maxScansPerSecond: 4 },
    )
    scannerRef.current = scanner
    scanner.start().catch((err) => setCameraError(cameraErrorMessage(err)))
    return () => { scanner.destroy() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleDecoded(qrToken: string) {
    // El callback del scanner se creó al montar y ve los valores iniciales de `loading` y `ticket`:
    // el guard real es este ref, que sigue ocupado mientras hay una consulta o una tarjeta en pantalla.
    if (busyRef.current) return
    busyRef.current = true
    setLoading(true)
    scannerRef.current?.pause()
    const token = qrToken.trim()
    const { eventDate: day, enqueue: queue, onEntry: countEntry } = latest.current
    try {
      const result = await cccdAttendance.getTicketByQr(token, day)
      setOfflineNote("")
      setTicket(result)
    } catch (err) {
      if (isNetworkError(err)) {
        // Sin red no se puede validar el boleto: se guarda el token y el API lo valida al reenviar.
        const added = queue({
          key: queueKey(token, day),
          qrToken: token,
          eventDate: day,
          method: "qr_scan",
          wristbandGiven: false,
          label: `QR …${token.slice(-6)}`,
        })
        if (added) {
          buzz()
          countEntry()
          setOfflineNote("Entrada guardada sin conexión. El boleto se validará al reenviar; si resulta inválido te avisaremos.")
        }
        setTimeout(() => {
          busyRef.current = false
          scannerRef.current?.start()
        }, 1200)
      } else {
        toastError(scanErrorMessage(err))
        busyRef.current = false
        scannerRef.current?.start()
      }
    } finally {
      setLoading(false)
    }
  }

  function scanNext() {
    setTicket(null)
    busyRef.current = false
    scannerRef.current?.start()
  }

  return (
    <div className="space-y-4">
      {!ticket && (
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-black dark:border-gray-800">
          <video ref={videoRef} className="aspect-square max-h-[50svh] w-full object-cover" muted playsInline />
          {loading && (
            <div
              role="status"
              aria-live="polite"
              className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-black/70 px-4 py-3 text-sm font-medium text-white backdrop-blur-sm"
            >
              <svg className="h-4 w-4 shrink-0 motion-safe:animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.25" />
                <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              Verificando boleto…
            </div>
          )}
        </div>
      )}
      {cameraError && (
        <Alert variant="error" title="No se pudo activar la cámara" message={cameraError} />
      )}
      {offlineNote && !ticket && (
        <div role="status" aria-live="polite" className={checkinConfirmPop}>
          <Alert variant="warning" title="Sin conexión" message={offlineNote} />
        </div>
      )}
      {ticket && (
        <>
          <TicketCard
            ticket={ticket}
            eventDate={eventDate}
            method="qr_scan"
            enqueue={enqueue}
            onEntry={onEntry}
            onCheckedIn={(checkIn) => setTicket((prev) => (prev ? { ...prev, checkIn } : prev))}
            onAutoAdvance={ticket.checkIn ? undefined : scanNext}
          />
          <button type="button" onClick={scanNext} className={`${bigBtn} border border-gray-300 dark:border-gray-700`}>
            Escanear siguiente
          </button>
        </>
      )}
    </div>
  )
}

function DocumentTab({ scope, eventDate, enqueue, onEntry }: { scope: Scope; eventDate: string; enqueue: Enqueue; onEntry: () => void }) {
  const [documentNumber, setDocumentNumber] = useState("")
  const [ticket, setTicket] = useState<ResultTicket | null>(null)
  const [error, setError] = useState("")

  const lookupMutation = useMutation({
    mutationFn: () => cccdAttendance.lookupByDocument(documentNumber.trim(), scope, eventDate),
    onSuccess: (result) => setTicket(result),
    onError: (err) => {
      const status = getErrorStatus(err)
      const message = isNetworkError(err)
        ? `${OFFLINE_MESSAGE} El escaneo QR sí queda en cola.`
        : status === 404
          ? "No hay un boleto con ese documento para este evento y día. Revisa el número o prueba con el QR."
          : status === 410
            ? "Este boleto fue revocado y ya no es válido."
            : getErrorMessage(err)
      setError(message)
      toastError(message)
    },
  })

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => { e.preventDefault(); setError(""); setTicket(null); lookupMutation.mutate() }}
        className="flex gap-2"
      >
        <label htmlFor="checkin-document-number" className="sr-only">Número de documento</label>
        <input
          id="checkin-document-number"
          className={inputCls}
          placeholder="Número de documento"
          value={documentNumber}
          onChange={(e) => setDocumentNumber(e.target.value)}
          required
        />
        <button type="submit" disabled={lookupMutation.isPending} className={`min-h-11 shrink-0 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60 ${btnFocus}`}>
          {lookupMutation.isPending ? "Buscando..." : "Buscar"}
        </button>
      </form>
      {error && <Alert variant="error" title={error.startsWith(OFFLINE_MESSAGE) ? "Sin conexión" : "No se encontró el boleto"} message={error} />}
      {ticket && (
        <TicketCard
          ticket={ticket}
          eventDate={eventDate}
          method="document_lookup"
          enqueue={enqueue}
          onEntry={onEntry}
          onCheckedIn={(checkIn) => setTicket((prev) => (prev ? { ...prev, checkIn } : prev))}
        />
      )}
    </div>
  )
}

function OnsiteTab({ scope, eventDate, onEntry }: { scope: Scope; eventDate: string; onEntry: () => void }) {
  const [teamQuery, setTeamQuery] = useState("")
  const [teamOptions, setTeamOptions] = useState<Array<{ id: string; name: string }>>([])
  const [teamSearched, setTeamSearched] = useState(false)
  const [teamId, setTeamId] = useState("")
  const [form, setForm] = useState({
    firstName: "", lastName: "", documentType: "CC", documentNumber: "",
    email: "", phone: "", relationshipToTeam: "", marketingConsent: false,
  })
  const [dataAuth, setDataAuth] = useState(false)
  const [error, setError] = useState("")
  const [result, setResult] = useState<{ firstName: string; lastName: string; alreadyCheckedIn: boolean } | null>(null)

  useEffect(() => {
    if (teamQuery.trim().length < 2) { setTeamOptions([]); setTeamSearched(false); return }
    setTeamSearched(false)
    const handle = setTimeout(() => {
      cccdAttendance.searchTeams(teamQuery.trim(), scope)
        .then((options) => { setTeamOptions(options); setTeamSearched(true) })
        .catch(() => { setTeamOptions([]); setTeamSearched(true) })
    }, 300)
    return () => clearTimeout(handle)
  }, [teamQuery, scope])

  const mutation = useMutation({
    mutationFn: () =>
      cccdAttendance.registerOnsite({
        ...scope,
        teamId,
        ...form,
        dataTreatmentAuthorization: dataAuth,
        eventDate,
      }),
    onSuccess: (res) => {
      if (!res.alreadyCheckedIn) onEntry()
      setResult({ firstName: res.firstName, lastName: res.lastName, alreadyCheckedIn: res.alreadyCheckedIn })
      setForm({ firstName: "", lastName: "", documentType: "CC", documentNumber: "", email: "", phone: "", relationshipToTeam: "", marketingConsent: false })
      setDataAuth(false)
      setTeamId("")
      setTeamQuery("")
    },
    onError: (err) => {
      const message = networkAware(err)
      setError(message)
      toastError(message)
    },
  })

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); setError(""); setResult(null); mutation.mutate() }}
      className="space-y-4"
    >
      <div>
        <label htmlFor="checkin-onsite-team" className="mb-1 block text-xs font-medium text-gray-500">Equipo al que apoya *</label>
        <input
          id="checkin-onsite-team"
          className={inputCls}
          placeholder="Buscar equipo por nombre..."
          value={teamId ? teamOptions.find((t) => t.id === teamId)?.name ?? teamQuery : teamQuery}
          onChange={(e) => { setTeamId(""); setTeamQuery(e.target.value) }}
          required
        />
        {teamOptions.length > 0 && !teamId && (
          <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800">
            {teamOptions.map((t) => (
              <button key={t.id} type="button" onClick={() => { setTeamId(t.id); setTeamQuery(t.name); setTeamOptions([]) }}
                className="flex min-h-11 w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
                {t.name}
              </button>
            ))}
          </div>
        )}
        {teamSearched && teamOptions.length === 0 && !teamId && (
          <p role="status" aria-live="polite" className="mt-1 text-xs text-gray-500 dark:text-gray-400">No se encontraron equipos con ese nombre.</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="checkin-onsite-firstName" className="sr-only">Nombre</label>
          <input id="checkin-onsite-firstName" required className={inputCls} placeholder="Nombre" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
        </div>
        <div>
          <label htmlFor="checkin-onsite-lastName" className="sr-only">Apellido</label>
          <input id="checkin-onsite-lastName" required className={inputCls} placeholder="Apellido" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="checkin-onsite-documentType" className="sr-only">Tipo de documento</label>
          <select id="checkin-onsite-documentType" className={inputCls} value={form.documentType} onChange={(e) => set("documentType", e.target.value)}>
            <option value="CC">Cédula de ciudadanía</option>
            <option value="TI">Tarjeta de identidad</option>
            <option value="CE">Cédula de extranjería</option>
            <option value="PA">Pasaporte</option>
          </select>
        </div>
        <div>
          <label htmlFor="checkin-onsite-documentNumber" className="sr-only">Número de documento</label>
          <input id="checkin-onsite-documentNumber" required className={inputCls} placeholder="Número de documento" value={form.documentNumber} onChange={(e) => set("documentNumber", e.target.value)} />
        </div>
      </div>
      <div>
        <label htmlFor="checkin-onsite-relationship" className="sr-only">Relación con el equipo</label>
        <input id="checkin-onsite-relationship" className={inputCls} placeholder="Relación con el equipo (ej. madre del piloto)" value={form.relationshipToTeam} onChange={(e) => set("relationshipToTeam", e.target.value)} />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="checkin-onsite-email" className="sr-only">Correo</label>
          <input id="checkin-onsite-email" type="email" className={inputCls} placeholder="Correo (opcional)" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div>
          <label htmlFor="checkin-onsite-phone" className="sr-only">Teléfono</label>
          <input id="checkin-onsite-phone" className={inputCls} placeholder="Teléfono (opcional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <input type="checkbox" checked={form.marketingConsent} onChange={(e) => set("marketingConsent", e.target.checked)} />
        Acepta ser contactado sobre futuros eventos similares
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <input type="checkbox" required checked={dataAuth} onChange={(e) => setDataAuth(e.target.checked)} />
        Autoriza el tratamiento de datos personales *
      </label>

      {error && <Alert variant="error" title="No se pudo registrar" message={error} />}
      {result && (
        <div className={checkinConfirmPop}>
          <Alert
            variant="success"
            title={result.alreadyCheckedIn ? "Ya tenía boleto — entrada ya registrada" : "Registrado e ingresado"}
            message={`${result.firstName} ${result.lastName} — entrada confirmada para hoy.`}
          />
        </div>
      )}

      {!teamId && (
        <p id="checkin-onsite-hint" className="text-sm text-gray-600 dark:text-gray-400">
          Elige el equipo de la lista de resultados para poder registrar la entrada.
        </p>
      )}
      <button type="submit" disabled={mutation.isPending || !teamId} aria-describedby={!teamId ? "checkin-onsite-hint" : undefined} className={`${bigBtn} bg-brand-500 text-white hover:bg-brand-600`}>
        {mutation.isPending ? "Registrando..." : "Registrar e ingresar"}
      </button>
    </form>
  )
}

function ConnectionBanner({
  online,
  pending,
  flushing,
  onFlush,
}: {
  online: boolean
  pending: number
  flushing: boolean
  onFlush: () => void
}) {
  if (online && pending === 0) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning-300 bg-warning-50 px-4 py-3 text-sm text-warning-800 dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-warning-300"
    >
      <span>
        {!online
          ? "Sin conexión. Los escaneos QR se guardan en este dispositivo y se envían al reconectar."
          : `${pending} ${pending === 1 ? "check-in pendiente" : "check-ins pendientes"} de enviar.`}
        {!online && pending > 0 ? ` En cola: ${pending}.` : ""}
      </span>
      {online && pending > 0 && (
        <button
          type="button"
          onClick={onFlush}
          disabled={flushing}
          className={`min-h-11 rounded-lg bg-warning-600 px-4 py-2 text-sm font-medium text-white hover:bg-warning-700 disabled:opacity-60 ${btnFocus}`}
        >
          {flushing ? "Enviando…" : "Enviar ahora"}
        </button>
      )}
    </div>
  )
}

export default function CheckInScanPage() {
  // Evento y día viven en la URL (/cccd/checkin/event/:id?date=): una recarga o un cambio de app
  // en la puerta no obliga a elegirlos de nuevo. El día arranca en hoy si cae en el rango del
  // evento. La lista de eventos solo aporta nombre y fechas: si no carga (sin red), el escaneo
  // sigue funcionando porque el QR ya trae el evento.
  const { eventId, regionalEventId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const kind = eventId ? "event" : "regional"
  const scopeId = eventId ?? regionalEventId ?? ""
  const scope: Scope = eventId ? { eventId } : { regionalEventId }
  const { data: summary, isLoading: summaryLoading } = useAttendanceSummary()
  const row = summary?.find((r) => r.scope === kind && r.id === scopeId)
  const dateRange = row ? { start: row.eventStartDate, end: row.eventEndDate } : null
  const eventDate = searchParams.get("date") ?? defaultEventDay(dateRange, cccdAttendance.todayIso())
  const [tab, setTab] = useState<Tab>("scan")
  const tallyKey = `cccd-checkin-count:${kind}:${scopeId}:${eventDate}`
  useEffect(() => {
    if (scopeId) rememberScope({ scope: kind, id: scopeId })
  }, [kind, scopeId])
  const [entryCount, setEntryCount] = useState(() => readCount(tallyKey))
  useEffect(() => {
    setEntryCount(readCount(tallyKey))
  }, [tallyKey])
  function countEntry() {
    const next = readCount(tallyKey) + 1
    writeCount(tallyKey, next)
    setEntryCount(next)
  }
  const { online, pending, flushing, flush, enqueue } = useCheckinQueue()
  const dateOutOfRange = !!dateRange && (eventDate < dateRange.start.slice(0, 10) || eventDate > dateRange.end.slice(0, 10))
  const days = dateRange ? daysBetween(dateRange.start, dateRange.end, 15) : []
  const scopeName = row?.name

  function setDay(date: string) {
    const params = new URLSearchParams(searchParams)
    params.set("date", date)
    setSearchParams(params, { replace: true })
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, { label: "CCCD", to: "/cccd" }, "Check-in / Asistencia"]}
      title="Check-in / Asistencia"
      compactOnMobile
      subtitle="Escanea el boleto QR, busca por cédula o registra en sitio."
      tabs={[
        { label: "Escanear", to: "/cccd/checkin", active: true },
        { label: "Reporte", to: "/cccd/checkin/report" },
      ]}
      metrics={[]}
    >
      <div className="mx-auto max-w-md space-y-5">
        <ConnectionBanner online={online} pending={pending.length} flushing={flushing} onFlush={() => void flush()} />

        <div className="sticky top-0 z-10 space-y-2 border-b border-gray-200 bg-white pb-3 dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-3">
            <Link
              to="/cccd/checkin"
              className="inline-flex min-h-11 items-center gap-1 rounded-lg pr-2 text-sm font-medium text-brand-500 hover:text-brand-600 focus:outline-hidden focus-visible:ring-3 focus-visible:ring-brand-500/20"
            >
              <span aria-hidden="true">←</span> Eventos
            </Link>
            <p className="shrink-0 whitespace-nowrap text-sm text-gray-500 tabular-nums dark:text-gray-400" aria-live="polite" title="Ingresos registrados en este dispositivo">
              {entryCount} {entryCount === 1 ? "ingreso" : "ingresos"}
            </p>
          </div>
          <h2 className="truncate text-base font-semibold text-gray-800 dark:text-white/90">
            {scopeName ?? (summaryLoading ? "Cargando evento…" : "Evento seleccionado")}
          </h2>
          {days.length > 1 && days.length <= 14 ? (
            <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Día del evento">
              {days.map((d) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={eventDate === d}
                  onClick={() => setDay(d)}
                  className={`min-h-11 shrink-0 rounded-lg px-4 py-2 text-sm font-medium transition focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 ${
                    eventDate === d ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  }`}
                >
                  {formatDay(d)}
                </button>
              ))}
            </div>
          ) : days.length > 14 || !dateRange ? (
            <div>
              <label htmlFor="checkin-event-date" className="sr-only">Día del evento</label>
              <input id="checkin-event-date" type="date" className={inputCls} value={eventDate} onChange={(e) => e.target.value && setDay(e.target.value)} />
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">{formatDay(eventDate)}</p>
          )}
        </div>

        {dateOutOfRange && dateRange && (
          <Alert
            variant="warning"
            title="Este día no corresponde al evento seleccionado"
            message={`El evento va del ${formatDay(dateRange.start)} al ${formatDay(dateRange.end)}. Los check-ins quedarán registrados con la fecha ${formatDay(eventDate)}.`}
          />
        )}

        {!summaryLoading && summary && !row && (
          <Alert variant="warning" title="No encontramos este evento" message="El enlace puede estar desactualizado. Vuelve a Eventos y elígelo de nuevo; mientras tanto el escaneo de QR sigue funcionando." />
        )}

          <>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Modo de ingreso</p>
            <div className="grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800" role="tablist" aria-label="Modo de ingreso">
              {([
                ["scan", "Escanear QR"],
                ["document", "Cédula"],
                ["onsite", "Registrar"],
              ] as [Tab, string][]).map(([key, label]) => (
                <button
                  key={key}
                  id={`checkin-tab-${key}`}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  aria-controls={`checkin-panel-${key}`}
                  onClick={() => setTab(key)}
                  className={`min-h-11 rounded-lg px-2 py-3 text-sm font-medium transition ${tab === key ? "bg-white text-gray-900 shadow-theme-xs dark:bg-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div id={`checkin-panel-${tab}`} role="tabpanel" aria-labelledby={`checkin-tab-${tab}`}>
              {tab === "scan" && <ScanTab eventDate={eventDate} enqueue={enqueue} onEntry={countEntry} />}
              {tab === "document" && <DocumentTab scope={scope} eventDate={eventDate} enqueue={enqueue} onEntry={countEntry} />}
              {tab === "onsite" && <OnsiteTab scope={scope} eventDate={eventDate} onEntry={countEntry} />}
            </div>
          </>
      </div>
    </ModuleLayout>
  )
}
